/**
 * @fileoverview Own shared display semantics for Intake identity, dates, labels, links, and selections. Queue, detail, reviews, and administration consume these conventions to present the same recorded facts consistently. Formatting does not establish business status or authorization; status vocabulary lives in `intakeStatuses.js` and server projections own the data.
 */

export const STATUS_OPTIONS = [
    ['new', 'New'],
    ['processing', 'Processing'],
    ['waiting_on_prospect', 'Waiting on Prospect'],
    ['needs_staff_review', 'Needs Staff Review'],
    ['ready_for_decision', 'Ready for Decision'],
    ['engagement_letter_to_be_sent', 'Engagement Letter to Be Sent'],
    ['accepted', 'Accepted'],
    ['rejected', 'Rejected'],
    ['converted', 'Converted'],
    ['duplicate', 'Duplicate'],
    ['archived', 'Archived'],
    ['error', 'Error'],
]

export const STAGE_OPTIONS = [
    ['ingest_dedupe', 'Ingest and Dedupe'],
    ['triage', 'Triage'],
    ['specialist_intake_review', 'Specialist Intake Review'],
    ['class_action_detection', 'Class Action Detection'],
    ['info_gathering', 'Information Gathering'],
    ['information_request', 'Information Request'],
    ['document_review', 'Document Review'],
    ['information_evaluation', 'Information Evaluation'],
    ['specialist_evaluation', 'Specialist Claim Evaluation'],
    ['final_brief', 'Final Brief'],
    ['decision', 'Decision'],
    ['conversion', 'Conversion'],
]

export const RUN_PIPELINE_STOP_STAGE_KEYS = new Set(['info_gathering', 'information_request', 'decision', 'conversion'])



export function titleize(value) {
    if (!value) return '-'
    return String(value)
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
}



export function getPromotionBatchStatusLabel(promotionBatch) {
    const status = typeof promotionBatch === 'string' ? promotionBatch : promotionBatch?.status
    if (!status) return ''
    const labels = {
        awaiting_close: 'Awaiting close',
        awaiting_score: 'Awaiting score',
        needs_basic_information: 'Needs basic info',
        needs_staff_review: 'Needs staff review',
        needs_duplicate_review: 'Needs duplicate review',
        scored_awaiting_batch_ranking: 'Scored · awaiting ranking',
        awaiting_batch_scoring: 'Batch scoring',
        scored_below_threshold: 'Below threshold',
        above_threshold_below_top_10: 'Below top 10',
        outreach_cap_reached: 'Outreach slots used',
        promoted_for_outreach: 'Promoted',
        waiting_on_human_email_acquisition: 'Needs email',
        active_class_action_bypass: 'Class action bypass',
        ineligible_for_batch_ranking: 'Resolved before ranking',
        archived_stale: 'Stale archived',
        manual_override: 'Manual override',
    }
    const detail = []
    if (promotionBatch?.batch_rank) detail.push(`#${promotionBatch.batch_rank}`)
    if (promotionBatch?.score !== null && promotionBatch?.score !== undefined) detail.push(`${Number(promotionBatch.score).toFixed(0)}`)
    return `${labels[status] || titleize(status)}${detail.length ? ` (${detail.join(' / ')})` : ''}`
}



export function getPromotionBatchStatusClass(promotionBatch) {
    const status = typeof promotionBatch === 'string' ? promotionBatch : promotionBatch?.status
    switch (status) {
        case 'promoted_for_outreach':
            return 'intake-badge--success'
        case 'waiting_on_human_email_acquisition':
            return 'intake-badge--danger'
        case 'active_class_action_bypass':
            return 'intake-badge--violet'
        case 'ineligible_for_batch_ranking':
            return 'intake-badge--muted'
        case 'awaiting_batch_scoring':
        case 'awaiting_close':
        case 'awaiting_score':
        case 'scored_awaiting_batch_ranking':
            return 'intake-badge--info'
        case 'needs_basic_information':
        case 'needs_staff_review':
        case 'needs_duplicate_review':
            return 'intake-badge--danger'
        case 'scored_below_threshold':
        case 'above_threshold_below_top_10':
        case 'outreach_cap_reached':
            return 'intake-badge--warning'
        case 'archived_stale':
        case 'manual_override':
            return 'intake-badge--muted'
        default:
            return 'intake-badge--info'
    }
}



export function formatDate(value) {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleString()
}



export function formatDateTimeLocal(value) {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    const offset = date.getTimezoneOffset() * 60000
    return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}



export function getStatusClass(status) {
    switch (status) {
        case 'new':
            return 'intake-badge--info'
        case 'processing':
        case 'waiting_on_prospect':
        case 'engagement_letter_to_be_sent':
            return 'intake-badge--warning'
        case 'needs_staff_review':
        case 'error':
            return 'intake-badge--danger'
        case 'ready_for_decision':
        case 'accepted':
        case 'converted':
            return 'intake-badge--success'
        case 'rejected':
        case 'duplicate':
        case 'archived':
            return 'intake-badge--muted'
        default:
            return 'intake-badge--info'
    }
}



export function getStageClass(stage) {
    switch (stage) {
        case 'ingest_dedupe':
            return 'intake-badge--warning'
        case 'triage':
            return 'intake-badge--cyan'
        case 'analysis':
        case 'specialist_intake_review':
        case 'specialist_evaluation':
            return 'intake-badge--blue'
        case 'class_action_detection':
            return 'intake-badge--violet'
        case 'info_gathering':
        case 'information_request':
        case 'information_evaluation':
            return 'intake-badge--sky'
        case 'document_review':
            return 'intake-badge--violet'
        case 'final_brief':
            return 'intake-badge--indigo'
        case 'decision':
            return 'intake-badge--teal'
        case 'conversion':
            return 'intake-badge--success'
        default:
            return 'intake-badge--muted'
    }
}



export function getDuplicateStatusLabel(lead) {
    const status = lead?.duplicate_review_status || 'not_duplicate'
    const count = Number(lead?.duplicate_candidate_count || 0)
    switch (status) {
        case 'confirmed_duplicate':
            return 'Duplicate'
        case 'likely_duplicate':
            return `Likely Duplicate${count ? ` (${count})` : ''}`
        case 'possible_duplicate':
            return `Possible Duplicate${count ? ` (${count})` : ''}`
        case 'merged':
            return 'Dedupe Merged'
        case 'not_duplicate':
            return count ? 'Not Duplicate' : 'No Duplicate Match'
        default:
            return titleize(status)
    }
}



export function getDuplicateStatusClass(status) {
    switch (status) {
        case 'confirmed_duplicate':
        case 'likely_duplicate':
            return 'intake-badge--danger'
        case 'possible_duplicate':
            return 'intake-badge--warning'
        case 'merged':
        case 'not_duplicate':
            return 'intake-badge--success'
        default:
            return 'intake-badge--muted'
    }
}



export function getRecordStatusClass(status) {
    switch (status) {
        case 'completed':
        case 'approved':
        case 'accepted':
        case 'reviewed':
        case 'sent':
            return 'intake-badge--success'
        case 'send_failed':
        case 'failed':
        case 'error':
            return 'intake-badge--danger'
        case 'sending':
        case 'running':
        case 'processing':
        case 'needs_review':
        case 'review_required':
            return 'intake-badge--warning'
        case 'skipped':
        case 'rejected':
        case 'archived':
            return 'intake-badge--muted'
        default:
            return 'intake-badge--info'
    }
}

export const STAGE_LABELS = STAGE_OPTIONS.reduce((labels, [key, label]) => {
    labels[key] = label
    return labels
}, {})
STAGE_LABELS.information_request = 'Information Request'



export function getStageLabel(stageKey) {
    return STAGE_LABELS[stageKey] || titleize(stageKey)
}



function normalizePipelineStage(stage, index) {
    if (Array.isArray(stage)) {
        return {
            key: stage[0],
            label: stage[1] || getStageLabel(stage[0]),
            order: index,
            agents: stage[2]?.agents || [],
        }
    }
    return {
        ...(stage || {}),
        key: stage?.key,
        label: stage?.label || getStageLabel(stage?.key),
        order: Number.isFinite(Number(stage?.order)) ? Number(stage.order) : index,
        agents: stage?.agents || [],
    }
}



export function getOrderedPipelineStages(pipelineStages = []) {
    const sourceStages = (pipelineStages || []).length ? pipelineStages : STAGE_OPTIONS
    return sourceStages
        .map(normalizePipelineStage)
        .filter(stage => stage.key && stage.enabled !== false)
        .sort((left, right) => Number(left.order || 0) - Number(right.order || 0))
}



export function getRunPipelineTargetStage(pipelineStages = [], currentStageKey = '') {
    const stages = getOrderedPipelineStages(pipelineStages)
    if (!stages.length) return null
    const currentIndex = stages.findIndex(stage => stage.key === currentStageKey)
    const startIndex = currentIndex >= 0 ? currentIndex : 0
    return stages.slice(startIndex).find(stage => RUN_PIPELINE_STOP_STAGE_KEYS.has(stage.key)) || stages[startIndex] || null
}



export function getRunPipelineActionLabel({ currentStageKey = '', pipelineStages = [], running = false, compact = false } = {}) {
    if (running) return 'Running Stages'
    if (compact) return 'Run'
    const targetStage = getRunPipelineTargetStage(pipelineStages, currentStageKey)
    if (!targetStage?.key) return 'Run Pipeline'
    const targetLabel = INFORMATION_REQUEST_STAGE_KEYS.includes(targetStage.key) ? 'Information Request' : targetStage.label || getStageLabel(targetStage.key)
    if (targetStage.key === currentStageKey) return `Run ${targetLabel} Stage`
    return `Run to ${targetLabel}`
}



export function getRunPipelineActionTitle({ currentStageKey = '', pipelineStages = [], disabledReason = '' } = {}) {
    if (disabledReason) return disabledReason
    const targetStage = getRunPipelineTargetStage(pipelineStages, currentStageKey)
    if (!targetStage?.key) return 'Run the automated intake pipeline for this lead.'
    const targetLabel = INFORMATION_REQUEST_STAGE_KEYS.includes(targetStage.key) ? 'Information Request' : targetStage.label || getStageLabel(targetStage.key)
    if (targetStage.key === currentStageKey) return `Run ${targetLabel} for this lead.`
    return `Run automated intake stages until this lead reaches ${targetLabel}.`
}



export function getPipedriveRecordUrl(lead) {
    if (lead?.pipedrive_deal_id) return `https://app.pipedrive.com/deal/${encodeURIComponent(lead.pipedrive_deal_id)}`
    if (lead?.pipedrive_lead_id) return `https://app.pipedrive.com/leads/inbox/${encodeURIComponent(lead.pipedrive_lead_id)}`
    return null
}



export function getRunTime(run) {
    return run?.completed_at || run?.started_at || run?.updated_at || run?.created_at || ''
}



export function getTimestamp(value) {
    if (!value) return 0
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? 0 : date.getTime()
}



export function pluralize(count, singular, plural = `${singular}s`) {
    return `${count} ${count === 1 ? singular : plural}`
}



export function getActivityDetail(parts) {
    return parts.filter(Boolean).join(' / ') || '-'
}



export function formatPercent(value) {
    if (value === null || value === undefined || value === '') return ''
    const number = Number(value)
    if (Number.isNaN(number)) return String(value)
    return `${Math.round(number * 100)}%`
}

export const INFORMATION_REQUEST_STAGE_KEYS = ['info_gathering', 'information_request']
